<template>
    <div
        v-if="props.status === 'loaded' || props.status === 'loading'"
        :class="$style.results"
    >
        <SearchCards :cards="gamesSearched" />
        <SearchCards :cards="groupsSearched" />
    </div>
    <div
        v-else-if="props.status === 'empty'"
        :class="$style.empty"
    >
        <img
            :src="EyesImg"
            alt="no_results"
            loading="lazy"
            :class="$style.no_results_img"
        />
        <div :class="$style.no_results_text">
            <Typography
                type="header"
                size="s"
                :responsive="false"
                :class="$style.no_results_title"
            >
                {{ $tr('search.empty_title') }}
            </Typography>
            <Typography
                size="s"
                :responsive="false"
                :class="$style.no_results_desc"
            >
                {{ $tr('search.empty_desc') }}
            </Typography>
        </div>

        <PromoCategoriesSwitchers position="center" />
    </div>
    <div
        v-else
        :class="$style.initial"
    >
        <PromoCategoriesSwitchers />
        <Typography
            type="caption_all-caps"
            :class="$style.title"
        >
            {{ $tr('search.top_games') }}
        </Typography>
        <SearchCards :cards="topGames" />
        <template v-if="recentlyPlayedGames?.length">
            <Typography
                type="caption_all-caps"
                :class="$style.title"
            >
                {{ $tr('search.recent_games') }}
            </Typography>
            <SearchCards :cards="recentlyPlayedGames" />
        </template>
    </div>
</template>
<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import { IconNameType } from '@/components_new/Icon/Icon.vue'
import Typography from '@/components_new/Typography.vue'
import { useTrendingData } from '@/query/use-trending-data'
import { ROUTE_NAMES, ROUTE_NAMES_GAME_GROUPS } from '@/router/constants'
import { useMyGamesStore } from '@/store/my-games/my-games-store'

import PromoCategoriesSwitchers from '../components/PromoCategoriesSwitchers.vue'
import SearchCards from '../components/SearchCards.vue'
import EyesImg from '../images/eyes.webp'
import { SearchResults } from '../types'

export type SearchStatusResult = 'initial' | 'loading' | 'empty' | 'loaded'

const FIRST_RECENT_GAMES = 3
const FIRST_TOP_GAMES = 3
const MAP_GROUPS_LINK_TO_ICON: Record<ROUTE_NAMES_GAME_GROUPS, IconNameType> = {
    [ROUTE_NAMES.CATEGORY]: 'all_games',
    [ROUTE_NAMES.SERIES]: 'all_series',
    [ROUTE_NAMES.TAG]: 'all_tags',
}

const props = defineProps<{
    searchResults?: SearchResults
    status?: SearchStatusResult
}>()

const myGamesStore = useMyGamesStore()
const { myUnzippedGames } = storeToRefs(myGamesStore)
const { unzippedTrendingWithGames } = useTrendingData({})
const recentlyPlayedGames = computed(() =>
    myUnzippedGames.value
        .slice(0, FIRST_RECENT_GAMES)
        .map((item) => ({ ...item, statusIcon: 'clock' as IconNameType })),
)

const topGames = computed(() => unzippedTrendingWithGames.value?.slice(0, FIRST_TOP_GAMES))

const gamesSearched = computed(() =>
    props.searchResults?.result
        ?.filter((item) => item.routeName === ROUTE_NAMES.GAME_PAGE)
        .map((item) => ({ hru: item.gameHru, preview: item.image, title: item.name, routeName: item.routeName })),
)
const groupsSearched = computed(() =>
    props.searchResults?.result
        ?.filter((item) => item.routeName !== ROUTE_NAMES.GAME_PAGE)
        .map((item) => ({
            hru: item.hru,
            title: item.name,
            routeName: item.routeName,
            previewIcon: MAP_GROUPS_LINK_TO_ICON[item.routeName],
        })),
)
</script>
<style module>
.results {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.title {
    color: var(--carbon-600);
}

.initial {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.empty {
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: center;
    justify-content: center;
    width: 90%;
    margin: 24px auto auto;
}

.no_results_text {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    text-align: center;
    padding: 0 12px;
    margin-bottom: 8px;
}

.no_results_desc {
    color: var(--white-60);
}

.no_results_img {
    width: 60px;
    height: 60px;
}

@media (--desktop-small-plus) {
    .initial {
        gap: 12px;
    }
    .empty {
        margin: 8px auto auto;
    }
    .results {
        gap: 4px;
    }
    .title {
        padding: 0 12px;
    }
}
</style>
